import { useEffect, useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import DensityMediumIcon from '@mui/icons-material/DensityMedium'
import DensitySmallIcon from '@mui/icons-material/DensitySmall'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import { GroupByPane } from '@/shared/groupBy'
import { ColumnsPane } from '@/runtime/ColumnsPane'
import { FiltersPane } from './FiltersPane'
import { SaveViewControl, SavedViewsBar } from '@/runtime/SavedViewsBar'
import { PinToggle } from '@/shared/components/CommandBar'
import type { ListChromeId, PinSlot } from '@/shared/form/commandPin'
import type { ListViewKind } from '@/shared/listViews/viewCandidates'
import type { Density } from '@/shared/density/densityTokens'
import {
  draftsEqual,
  type ListSettingsDialogProps,
  type ListSettingsTabId,
  type ListSettingsValue,
} from './types'

function cloneSettings(value: ListSettingsValue): ListSettingsValue {
  return {
    ...value,
    availableViews: [...value.availableViews],
    filters: { ...value.filters },
    groupByFields: [...value.groupByFields],
    groupByPalette: [...value.groupByPalette],
    columns: [...value.columns],
    columnPrefs: {
      order: [...value.columnPrefs.order],
      widths: { ...value.columnPrefs.widths },
      hidden: [...value.columnPrefs.hidden],
    },
    pins: { ...value.pins },
  }
}

export function ListSettingsDialog({
  open,
  initialTab,
  value,
  draftEpoch,
  onClose,
  onApply,
  onTabChange,
  savedViews,
  activeViewId,
  onApplySavedView,
  onRemoveSavedView,
  onSaveView,
  filterFields = [],
  filterOptionsByField = {},
  compositeFilterMeta = {},
  connectionId = '',
}: ListSettingsDialogProps) {
  const [tab, setTab] = useState<ListSettingsTabId>(initialTab ?? 'appearance')
  const [draft, setDraft] = useState<ListSettingsValue>(() => cloneSettings(value))

  useEffect(() => {
    if (!open) return
    setDraft(cloneSettings(value))
    // Reset draft on open / draftEpoch only — not on every value identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftEpoch])

  useEffect(() => {
    if (!open) return
    setTab(initialTab ?? 'appearance')
  }, [open, initialTab])

  const setPin = (id: ListChromeId, slot: PinSlot) => {
    setDraft((prev) => ({ ...prev, pins: { ...prev.pins, [id]: slot } }))
  }

  const pinRow = (id: ListChromeId, label: string, control: ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>{control}</Box>
      <PinToggle label={label} pinned={draft.pins[id] === 'bar'} onChange={(slot) => setPin(id, slot)} />
    </Box>
  )

  const viewToggle = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={draft.view}
      onChange={(_, next: ListViewKind | null) => {
        if (next) setDraft((prev) => ({ ...prev, view: next }))
      }}
    >
      {draft.availableViews.includes('list') && (
        <ToggleButton value="list" aria-label="List view" sx={{ textTransform: 'none' }}>
          <TableRowsIcon fontSize="small" />
          List
        </ToggleButton>
      )}
      {draft.availableViews.includes('kanban') && (
        <ToggleButton value="kanban" aria-label="Kanban view" sx={{ textTransform: 'none' }}>
          <ViewKanbanIcon fontSize="small" />
          Kanban
        </ToggleButton>
      )}
      {draft.availableViews.includes('tree') && (
        <ToggleButton value="tree" aria-label="Tree view" sx={{ textTransform: 'none' }}>
          <AccountTreeIcon fontSize="small" />
          Tree
        </ToggleButton>
      )}
    </ToggleButtonGroup>
  )

  const densityToggle = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={draft.density}
      onChange={(_, next: Density | null) => {
        if (next) setDraft((prev) => ({ ...prev, density: next }))
      }}
    >
      <ToggleButton value="comfortable" aria-label="Comfortable density" sx={{ textTransform: 'none' }}>
        <DensityMediumIcon fontSize="small" />
        Comfortable
      </ToggleButton>
      <ToggleButton value="compact" aria-label="Compact density" sx={{ textTransform: 'none' }}>
        <DensitySmallIcon fontSize="small" />
        Compact
      </ToggleButton>
    </ToggleButtonGroup>
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      transitionDuration={0}
      aria-labelledby="list-settings-title"
    >
      <DialogTitle id="list-settings-title">List settings</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, next: ListSettingsTabId) => {
          setTab(next)
          onTabChange?.(next)
        }}
        aria-label="List settings sections"
      >
        <Tab value="appearance" label="Appearance" />
        <Tab value="filters" label="Filters" />
        <Tab value="grouping" label="Grouping" />
        <Tab value="columns" label="Columns" />
        <Tab value="views" label="Views" />
      </Tabs>
      <DialogContent>
        {tab === 'appearance' && (
          <>
            {draft.availableViews.length > 1 && pinRow('View', 'View', viewToggle)}
            {pinRow('Density', 'Density', densityToggle)}
          </>
        )}
        {tab === 'filters' && (
          <>
            <FiltersPane
              fields={filterFields}
              values={draft.filters}
              optionsByField={filterOptionsByField}
              compositeMeta={compositeFilterMeta}
              connectionId={connectionId}
              onChange={(filters) => setDraft((prev) => ({ ...prev, filters }))}
            />
            {filterFields.length > 0 && pinRow('Filters', 'Filters', null)}
          </>
        )}
        {tab === 'grouping' && (
          <>
            <GroupByPane
              fields={value.groupByPalette}
              draft={draft.groupByFields}
              onDraftChange={(groupByFields) => setDraft((prev) => ({ ...prev, groupByFields }))}
            />
            {pinRow('GroupBy', 'Group by', null)}
          </>
        )}
        {tab === 'columns' && (
          <>
            <ColumnsPane
              columns={value.columns}
              hidden={draft.columnPrefs.hidden}
              onToggle={(name) => {
                setDraft((prev) => {
                  const hidden = prev.columnPrefs.hidden.includes(name)
                    ? prev.columnPrefs.hidden.filter((n) => n !== name)
                    : [...prev.columnPrefs.hidden, name]
                  return { ...prev, columnPrefs: { ...prev.columnPrefs, hidden } }
                })
              }}
              onReset={() =>
                setDraft((prev) => ({
                  ...prev,
                  columnPrefs: { order: [], widths: {}, hidden: [] },
                }))
              }
            />
            {pinRow('Columns', 'Columns', null)}
          </>
        )}
        {tab === 'views' && (
          <>
            <SaveViewControl
              onSave={(name) => {
                onApply(draft)
                onSaveView(name, draft)
              }}
            />
            <SavedViewsBar
              views={savedViews}
              activeId={activeViewId}
              onApply={onApplySavedView}
              onRemove={onRemoveSavedView}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={draftsEqual(draft, value)} onClick={() => onApply(draft)}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export type { ListSettingsDialogProps }
